// ── Types ──────────────────────────────────────────────────────────────────────

export type FPBandKind = "CHAMPION" | "TOP" | "MID" | "RELEGATION";

/** A contiguous run of table positions sharing a band. */
export interface FPBand {
  kind: FPBandKind;
  label: string;
  /** CSS custom-property name for this band's accent color, or null for mid-table (no color). */
  colorVar: string | null;
  /** 1-indexed, inclusive. */
  startPosition: number;
  /** 1-indexed, inclusive. */
  endPosition: number;
}

export interface FPBandInput {
  clubCount: number;
  topTierSize: number | null;
  relegationSize: number | null;
  /** Whether the champion bonus is scored for this competition — when true, position 1 is
   * carved out of the top-tier band into its own single-row band, distinct from qualifying
   * for the top tier generally (the champion bonus rewards predicting the exact winner). */
  championEnabled: boolean;
  /** Admin-chosen names, e.g. "Champions League" / "Relegation Zone". Null/blank falls back to fallbackLabels. */
  topTierLabel: string | null;
  relegationLabel: string | null;
  /** i18n strings supplied by the caller — keeps this module framework-agnostic. */
  fallbackLabels: { champion: string; top: string; mid: string; relegation: string };
}

// ── Color tokens ───────────────────────────────────────────────────────────────

const COLOR_CHAMPION = "--color-fp-band-champion";
const COLOR_TOP = "--color-fp-band-primary";
const COLOR_RELEGATION = "--color-fp-band-relegation";

// ── Helpers ────────────────────────────────────────────────────────────────────

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ── Band computation ───────────────────────────────────────────────────────────

/**
 * Contiguous band ranges, ordered top → bottom. Total function — never throws, always
 * covers exactly [1, clubCount] with no gaps or overlaps (or [] when clubCount <= 0).
 *
 * Top tier wins over relegation on overlapping/invalid config (e.g. topTierSize +
 * relegationSize > clubCount); mid-table is whatever's left and is omitted when empty.
 */
export function computeBands(input: FPBandInput): FPBand[] {
  const { clubCount, championEnabled, fallbackLabels } = input;
  if (clubCount <= 0) return [];

  const effTop = clamp(input.topTierSize ?? 0, 0, clubCount);
  const champion = championEnabled && clubCount >= 1;
  // Positions reserved by champion+top combined — relegation must clamp against this, not
  // just effTop, or a 0-topTierSize + championEnabled config lets relegation's zone claim
  // position 1 too (champion always owns position 1 outright, even with no top tier).
  const reservedTop = champion ? Math.max(effTop, 1) : effTop;
  const effReleg = clamp(input.relegationSize ?? 0, 0, Math.max(0, clubCount - reservedTop));

  const bands: FPBand[] = [];

  if (champion) {
    bands.push({ kind: "CHAMPION", label: fallbackLabels.champion, colorVar: COLOR_CHAMPION, startPosition: 1, endPosition: 1 });
  }

  // Position 1 is champion's alone once carved out — the remaining top-tier band (if any)
  // starts at 2, and shrinks to nothing when topTierSize is exactly 1 (no distinct
  // "qualified but not champion" zone left to show).
  const topStart = champion ? 2 : 1;
  if (effTop >= topStart) {
    const label = input.topTierLabel?.trim() || fallbackLabels.top;
    bands.push({ kind: "TOP", label, colorVar: COLOR_TOP, startPosition: topStart, endPosition: effTop });
  }

  const midStart = reservedTop + 1;
  const midEnd = clubCount - effReleg;
  if (midEnd >= midStart) {
    bands.push({ kind: "MID", label: fallbackLabels.mid, colorVar: null, startPosition: midStart, endPosition: midEnd });
  }

  if (effReleg > 0) {
    const label = input.relegationLabel?.trim() || fallbackLabels.relegation;
    bands.push({ kind: "RELEGATION", label, colorVar: COLOR_RELEGATION, startPosition: clubCount - effReleg + 1, endPosition: clubCount });
  }

  return bands;
}

/** One entry per position — result[i] is the band for position i + 1. Always length === clubCount. */
export function computePositionBands(input: FPBandInput): FPBand[] {
  const bands = computeBands(input);
  const result: FPBand[] = [];
  for (const band of bands) {
    for (let pos = band.startPosition; pos <= band.endPosition; pos++) {
      result[pos - 1] = band;
    }
  }
  return result;
}
